import React, { useContext, useState, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MainCard from '../berry/ui-component/cards/MainCard.jsx';
import UserContext from '../context/user.jsx';
import api_ama_media from '../api/ama_media.js';
import cronstrue from 'cronstrue';

function cron_to_description(expr) {
	const e = (expr || '').trim();
	if (!e) return e;
	try {
		const desc = cronstrue.toString(e, { verbose: true , throwExceptionOnParseError: false });
		if (desc && !/error|invalid/i.test(desc)) return desc;
		return e;
	} catch (_) {
		return e;
	}
}

function cron_parts_to_expression(minute, hour, day_of_month, month, day_of_week) {
	return [minute, hour, day_of_month, month, day_of_week].join(' ');
}

function cron_expression_to_parts(expr) {
	const parts = (expr || '').trim().split(/\s+/).slice(0, 5);
	const num = (str, min, max) => {
		const n = parseInt(str, 10);
		return Number.isInteger(n) && n >= min && n <= max ? String(n) : '*';
	};
	return {
		minute: num(parts[0], 0, 59),
		hour: num(parts[1], 0, 23),
		day_of_month: num(parts[2], 1, 31),
		month: num(parts[3], 1, 12),
		day_of_week: num(parts[4], 0, 7),
	};
}

const CRON_DEFAULT = '*';

export default function AmaMediaSettings() {
	const { session } = useContext(UserContext);
	const [stopwords_count, set_stopwords_count] = useState(0);
	const [stopwords_upload_loading, set_stopwords_upload_loading] = useState(false);
	const stopwords_file_ref = useRef(null);
	const [schedule_items, set_schedule_items] = useState([]);
	const [cron_minute, set_cron_minute] = useState(CRON_DEFAULT);
	const [cron_hour, set_cron_hour] = useState(CRON_DEFAULT);
	const [cron_day_of_month, set_cron_day_of_month] = useState(CRON_DEFAULT);
	const [cron_month, set_cron_month] = useState(CRON_DEFAULT);
	const [cron_day_of_week, set_cron_day_of_week] = useState(CRON_DEFAULT);
	const [cron_custom, set_cron_custom] = useState('');
	const [schedule_label, set_schedule_label] = useState('');
	const [editing_id, set_editing_id] = useState(null);
	const [run_loading, set_run_loading] = useState(false);
	const [save_loading, set_save_loading] = useState(false);
	const [posts_to_retrieve, set_posts_to_retrieve] = useState(20);
	const [reels_to_retrieve, set_reels_to_retrieve] = useState(20);
	const [settings_save_loading, set_settings_save_loading] = useState(false);

	useEffect(() => {
		if (!session) return;
		api_ama_media.stopwords_count({ session }).then((r) => set_stopwords_count(r.count ?? 0)).catch(console.error);
		api_ama_media.schedule_get({ session }).then((r) => set_schedule_items(r.items || [])).catch(console.error);
		api_ama_media.settings_get({ session }).then((r) => {
			set_posts_to_retrieve(r.posts_to_retrieve ?? 20);
			set_reels_to_retrieve(r.reels_to_retrieve ?? 20);
		}).catch(console.error);
	}, [session]);

	const save_retrieval_settings = () => {
		set_settings_save_loading(true);
		api_ama_media.settings_save({ session, posts_to_retrieve, reels_to_retrieve })
			.then((r) => {
				set_posts_to_retrieve(r.posts_to_retrieve);
				set_reels_to_retrieve(r.reels_to_retrieve);
			})
			.catch(console.error)
			.finally(() => set_settings_save_loading(false));
	};

	const on_stopwords_file_chosen = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			const text = (reader.result || '').trim();
			const words = [...new Set(
				text.split(/\r?\n/).flatMap((line) => line.split(',').map((s) => s.trim()).filter(Boolean))
			)];
			set_stopwords_upload_loading(true);
			api_ama_media.stopwords_replace({ session, words })
				.then(() => api_ama_media.stopwords_count({ session }).then((r) => set_stopwords_count(r.count ?? 0)))
				.catch(console.error)
				.finally(() => {
					set_stopwords_upload_loading(false);
					e.target.value = '';
				});
		};
		reader.readAsText(file);
	};

	const run_now = () => {
		set_run_loading(true);
		api_ama_media
			.run_scrape({ session })
			.then(() => {})
			.catch(console.error)
			.finally(() => set_run_loading(false));
	};

	const add_schedule = () => {
		const cron_expression = (cron_custom.trim() || cron_parts_to_expression(cron_minute, cron_hour, cron_day_of_month, cron_month, cron_day_of_week)).trim();
		if (!cron_expression) return;
		set_save_loading(true);
		api_ama_media
			.schedule_save({ session, cron_expression, label: schedule_label.trim() })
			.then((r) => {
				set_schedule_items((prev) => [
					...prev,
					{ _id: r.id, cron_expression, label: schedule_label.trim() },
				]);
				set_cron_minute(CRON_DEFAULT);
				set_cron_hour(CRON_DEFAULT);
				set_cron_day_of_month(CRON_DEFAULT);
				set_cron_month(CRON_DEFAULT);
				set_cron_day_of_week(CRON_DEFAULT);
				set_cron_custom('');
				set_schedule_label('');
			})
			.catch(console.error)
			.finally(() => set_save_loading(false));
	};

	const update_schedule = () => {
		if (!editing_id) return;
		const cron_expression = (cron_custom.trim() || cron_parts_to_expression(cron_minute, cron_hour, cron_day_of_month, cron_month, cron_day_of_week)).trim();
		if (!cron_expression) return;
		set_save_loading(true);
		api_ama_media
			.schedule_save({ session, id: editing_id, cron_expression, label: schedule_label.trim() })
			.then(() => {
				set_schedule_items((prev) =>
					prev.map((s) =>
						String(s._id) === String(editing_id)
							? { ...s, cron_expression, label: schedule_label.trim() }
							: s
					)
				);
				set_editing_id(null);
				set_cron_minute(CRON_DEFAULT);
				set_cron_hour(CRON_DEFAULT);
				set_cron_day_of_month(CRON_DEFAULT);
				set_cron_month(CRON_DEFAULT);
				set_cron_day_of_week(CRON_DEFAULT);
				set_cron_custom('');
				set_schedule_label('');
			})
			.catch(console.error)
			.finally(() => set_save_loading(false));
	};

	const delete_schedule = (id) => {
		api_ama_media.schedule_delete({ session, id }).then(() => {
			set_schedule_items((prev) => prev.filter((s) => String(s._id) !== String(id)));
		}).catch(console.error);
	};

	const edit_schedule = (s) => {
		set_editing_id(s._id);
		const expr = (s.cron_expression || s.cron || '').trim();
		const parts = cron_expression_to_parts(expr);
		set_cron_minute(parts.minute);
		set_cron_hour(parts.hour);
		set_cron_day_of_month(parts.day_of_month);
		set_cron_month(parts.month);
		set_cron_day_of_week(parts.day_of_week);
		set_cron_custom(expr);
		set_schedule_label(s.label || '');
	};

	const reset_cron_form = () => {
		set_editing_id(null);
		set_cron_minute(CRON_DEFAULT);
		set_cron_hour(CRON_DEFAULT);
		set_cron_day_of_month(CRON_DEFAULT);
		set_cron_month(CRON_DEFAULT);
		set_cron_day_of_week(CRON_DEFAULT);
		set_cron_custom('');
		set_schedule_label('');
	};

	return (
		<>
			<Box sx={{ mb: 3 }}>
				<Button
					variant="contained"
					onClick={run_now}
					disabled={run_loading}
					sx={{ mb: 2 }}
				>
					{run_loading ? 'Running...' : 'Run now'}
				</Button>
			</Box>

			<MainCard title="Retrieval limits" sx={{ mb: 3 }}>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
					<TextField
						size="small"
						label="Posts to retrieve"
						type="number"
						value={posts_to_retrieve}
						onChange={(e) => set_posts_to_retrieve(parseInt(e.target.value, 10) || 20)}
						inputProps={{ min: 1, max: 100 }}
						sx={{ width: 160 }}
					/>
					<TextField
						size="small"
						label="Reels to retrieve"
						type="number"
						value={reels_to_retrieve}
						onChange={(e) => set_reels_to_retrieve(parseInt(e.target.value, 10) || 20)}
						inputProps={{ min: 1, max: 100 }}
						sx={{ width: 160 }}
					/>
					<Button
						variant="outlined"
						size="small"
						disabled={settings_save_loading}
						onClick={save_retrieval_settings}
					>
						{settings_save_loading ? 'Saving...' : 'Save'}
					</Button>
				</Box>
			</MainCard>

			<MainCard title="Stopwords" sx={{ mb: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<Box>Stopwords: {stopwords_count}</Box>
					<input
						type="file"
						accept=".txt,.csv"
						ref={stopwords_file_ref}
						onChange={on_stopwords_file_chosen}
						style={{ display: 'none' }}
					/>
					<Button
						variant="outlined"
						size="small"
						disabled={stopwords_upload_loading}
						onClick={() => stopwords_file_ref.current?.click()}
					>
						{stopwords_upload_loading ? 'Uploading...' : 'Replace from file'}
					</Button>
				</Box>
			</MainCard>

			<MainCard title="Schedule">
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Cron</TableCell>
								<TableCell>Label</TableCell>
								<TableCell align="right"></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{schedule_items.map((s) => {
								const raw = s.cron_expression || s.cron || '';
								const desc = cron_to_description(raw);
								return (
								<TableRow key={s._id}>
									<TableCell>
										<Box>{desc}</Box>
										{raw && <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{raw}</Box>}
									</TableCell>
									<TableCell>{s.label || '-'}</TableCell>
									<TableCell align="right">
										<Button size="small" onClick={() => edit_schedule(s)}>
											Edit
										</Button>
										<IconButton size="small" color="error" onClick={() => delete_schedule(s._id)}>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							); })}
						</TableBody>
					</Table>
				</TableContainer>
				<Box sx={{ mt: 2 }}>
					<TextField
						size="small"
						placeholder="Custom cron (optional, e.g. */5 9 * * 1-5)"
						value={cron_custom}
						onChange={(e) => set_cron_custom(e.target.value)}
						fullWidth
						sx={{ mb: 2 }}
					/>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
						<FormControl size="small" sx={{ minWidth: 100 }}>
							<InputLabel id="cron-minute">Minute</InputLabel>
							<Select labelId="cron-minute" value={cron_minute} label="Minute" onChange={(e) => set_cron_minute(e.target.value)}>
								<MenuItem value="*">Every</MenuItem>
								{Array.from({ length: 60 }, (_, i) => (
									<MenuItem key={i} value={String(i)}>{i}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 100 }}>
							<InputLabel id="cron-hour">Hour</InputLabel>
							<Select labelId="cron-hour" value={cron_hour} label="Hour" onChange={(e) => set_cron_hour(e.target.value)}>
								<MenuItem value="*">Every</MenuItem>
								{Array.from({ length: 24 }, (_, i) => (
									<MenuItem key={i} value={String(i)}>{i}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 100 }}>
							<InputLabel id="cron-day">Day</InputLabel>
							<Select labelId="cron-day" value={cron_day_of_month} label="Day" onChange={(e) => set_cron_day_of_month(e.target.value)}>
								<MenuItem value="*">Every</MenuItem>
								{Array.from({ length: 31 }, (_, i) => (
									<MenuItem key={i} value={String(i + 1)}>{i + 1}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 120 }}>
							<InputLabel id="cron-month">Month</InputLabel>
							<Select labelId="cron-month" value={cron_month} label="Month" onChange={(e) => set_cron_month(e.target.value)}>
								<MenuItem value="*">Every</MenuItem>
								{Array.from({ length: 12 }, (_, i) => (
									<MenuItem key={i} value={String(i + 1)}>{i + 1}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 110 }}>
							<InputLabel id="cron-weekday">Weekday</InputLabel>
							<Select labelId="cron-weekday" value={cron_day_of_week} label="Weekday" onChange={(e) => set_cron_day_of_week(e.target.value)}>
								<MenuItem value="*">Every</MenuItem>
								<MenuItem value="0">Sun (0)</MenuItem>
								<MenuItem value="1">Mon</MenuItem>
								<MenuItem value="2">Tue</MenuItem>
								<MenuItem value="3">Wed</MenuItem>
								<MenuItem value="4">Thu</MenuItem>
								<MenuItem value="5">Fri</MenuItem>
								<MenuItem value="6">Sat</MenuItem>
								<MenuItem value="7">Sun (7)</MenuItem>
							</Select>
						</FormControl>
					</Box>
					<Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
						{(() => {
							const current_expr = cron_custom.trim() || cron_parts_to_expression(cron_minute, cron_hour, cron_day_of_month, cron_month, cron_day_of_week);
							const desc = current_expr ? cron_to_description(current_expr) : '';
							return (
								<>
									{current_expr && <Box>Cron: {current_expr}</Box>}
									{desc && desc !== current_expr && <Box>{desc}</Box>}
								</>
							);
						})()}
					</Box>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
						<TextField
							size="small"
							placeholder="Label"
							value={schedule_label}
							onChange={(e) => set_schedule_label(e.target.value)}
							sx={{ minWidth: 160 }}
						/>
						{editing_id ? (
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button variant="contained" size="small" onClick={update_schedule} disabled={save_loading}>
									Update
								</Button>
								<Button variant="outlined" size="small" onClick={reset_cron_form}>
									Cancel
								</Button>
							</Box>
						) : (
							<Button variant="outlined" size="small" onClick={add_schedule} disabled={save_loading} sx={{ height: 40 }}>
								Add
							</Button>
						)}
					</Box>
				</Box>
			</MainCard>
		</>
	);
}
